/**
 * `ready` and `archived` are the simplified library statuses (2026-09 refinement). `approved` remains an
 * accepted alias of `ready` for rows written before the transition; readers must treat both as ready.
 */
export type ProofMetricStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'ready'
  | 'expired'
  | 'revoked'
  | 'archived';

export type ProofMetricType = 'percent_lift' | 'absolute' | 'rate' | 'count' | 'qualitative';

export type ProofType =
  | 'performance_metric'
  | 'benchmark'
  | 'case_study'
  | 'survey'
  | 'customer_review'
  | 'qualitative_claim';

export type ProofStrength = 'hard_metric' | 'case_study' | 'survey' | 'social_proof' | 'qualitative';

export type ProofOutreachScope = 'cold_dispensary_outreach' | 'warm_nurture' | 'sales_followup';

export type ProofClaimTextVariants = Record<string, string>;

export interface ProofClaimVariantPolicy {
  policy_id: string;
  policy_revision: number;
  claim_hash: string;
  external_copy_policy: 'location_free';
  approved_by: string;
  approved_at: string;
  approval_basis_ref?: string;
  clearance_notes?: string;
  attested_location_free?: boolean;
  source_identity_removed?: boolean;
  revoked_at?: string | null;
}

export type ProofClaimVariantPolicies = Record<string, ProofClaimVariantPolicy>;

export type ProofSourceVerification = Record<string, unknown>;

export type ProofNumericEvidence = Record<string, unknown>;

export type ProofIngestOrigin = 'legacy_manual' | 'manual' | 'ai_extracted' | 'extracted' | 'analytics';

export type ProofSourcePeriodDateKind = 'observed' | 'mixed' | 'unknown';

export type ProofSourceType =
  | 'performance_brief'
  | 'pulse_report'
  | 'internal_study'
  | 'customer_provided'
  | 'benchmark_aggregate'
  | 'public_web';

export type ProofAnonymization = 'named' | 'anonymized' | 'aggregate_only';

export type PublicProofSlot =
  | 'basket_lift_pct'
  | 'ad_revenue_annual_range'
  | 'ad_response_multiplier';

export interface PublicBasketLiftPayload {
  min: number;
  max: number;
  median: number;
  display: string;
}

export interface PublicAdRevenueAnnualRangePayload {
  min: number;
  max: number;
  display: string;
}

export interface PublicAdResponseMultiplierPayload {
  value: number;
  display: string;
}

export interface PublicProofPayloadBySlot {
  basket_lift_pct: PublicBasketLiftPayload;
  ad_revenue_annual_range: PublicAdRevenueAnnualRangePayload;
  ad_response_multiplier: PublicAdResponseMultiplierPayload;
}

export type PublicProofPayload = PublicProofPayloadBySlot[PublicProofSlot];

export type StoredPublicProofPayload = PublicProofPayload | Record<string, never>;

export type CampaignStatus = 'active' | 'complete';

export type ReviewDueType =
  | 'improvement_campaign_readout'
  | 'stabilization'
  | 'qbr'
  | 'renewal_planning'
  | 'at_risk_review';

export type ReviewDueStatus = 'open' | 'scheduled' | 'completed';

export type ReviewDueTriggerSource = 'manual' | 'auto';

export type ProofMetricTriggerSource = 'manual' | 'auto_seed';

export type ProofSeedTier =
  | 'case_study_url'
  | 'dashboard'
  | 'manifest'
  | 'pulse_sessions'
  | 'pulse_spotlight'
  | 'pulse_checkout';

export interface ProofMetric {
  id: string;
  proof_metric_id: string;
  version: number;
  status: ProofMetricStatus;
  label_short: string;
  claim_text: string;
  value_display: string;
  metric_type: ProofMetricType;
  proof_type: ProofType | null;
  proof_strength: ProofStrength | null;
  time_window: string;
  source_type: ProofSourceType;
  source_ref: string;
  claim_text_variants: ProofClaimTextVariants;
  claim_variant_policies?: ProofClaimVariantPolicies;
  source_verification: ProofSourceVerification;
  source_period_start: string | null;
  source_period_end: string | null;
  ingest_origin?: ProofIngestOrigin;
  source_period_date_kind?: ProofSourcePeriodDateKind;
  source_published_at?: string | null;
  source_fetched_at?: string | null;
  numeric_evidence?: ProofNumericEvidence | null;
  claim_limitations?: string[];
  claim_fingerprint?: string | null;
  claim_fingerprint_schema_version?: string | null;
  human_verified_by?: string | null;
  human_verified_at?: string | null;
  human_verified_fingerprint?: string | null;
  human_verification_schema_version?: string | null;
  evidence_set_id?: string | null;
  predecessor_proof_id?: string | null;
  archived_at?: string | null;
  archived_by?: string | null;
  archive_reason?: string | null;
  successor_proof_id?: string | null;
  location_ids: string[];
  anonymization: ProofAnonymization;
  jurisdiction_tags: string[];
  channels_allowed: string[];
  value_areas: string[];
  outreach_approved_by: string | null;
  outreach_approved_at: string | null;
  outreach_approval_basis_ref: string | null;
  outreach_scope: ProofOutreachScope | null;
  outreach_clearance_notes: string | null;
  outreach_revoked_at: string | null;
  named_use_approval_ref: string | null;
  /** Operator-set flag: this customer is fine being named in email/website use (replaces named_use_approval_ref). */
  named_use_ok?: boolean;
  /** Store labels resolved server-side for library rows (never persisted). */
  location_labels?: string[];
  public_slot?: PublicProofSlot | null;
  public_payload?: StoredPublicProofPayload;
  public_approved_by?: string[];
  public_approved_at?: string | null;
  public_approval_basis_ref?: string | null;
  expires_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  content_queue_ids: string[];
  trigger_source?: ProofMetricTriggerSource;
  trigger_key?: string | null;
  seed_tier?: ProofSeedTier | null;
  created_at: string;
  updated_at: string;
}

export interface ProofMetricInsert {
  proof_metric_id: string;
  version?: number;
  status?: ProofMetricStatus;
  label_short: string;
  claim_text: string;
  value_display: string;
  metric_type: ProofMetricType;
  proof_type?: ProofType | null;
  proof_strength?: ProofStrength | null;
  time_window: string;
  source_type: ProofSourceType;
  source_ref: string;
  claim_text_variants?: ProofClaimTextVariants;
  source_verification?: ProofSourceVerification;
  source_period_start: string;
  source_period_end: string;
  location_ids?: string[];
  anonymization?: ProofAnonymization;
  jurisdiction_tags?: string[];
  channels_allowed?: string[];
  value_areas?: string[];
  outreach_approved_by?: string | null;
  outreach_approved_at?: string | null;
  outreach_approval_basis_ref?: string | null;
  outreach_scope?: ProofOutreachScope | null;
  outreach_clearance_notes?: string | null;
  outreach_revoked_at?: string | null;
  named_use_approval_ref?: string | null;
  public_slot?: PublicProofSlot | null;
  public_payload?: StoredPublicProofPayload;
  public_approved_by?: string[];
  public_approved_at?: string | null;
  public_approval_basis_ref?: string | null;
  expires_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  content_queue_ids?: string[];
  trigger_source?: ProofMetricTriggerSource;
  trigger_key?: string | null;
  seed_tier?: ProofSeedTier | null;
}

export interface ProofMetricUpdate {
  status?: ProofMetricStatus;
  label_short?: string;
  claim_text?: string;
  value_display?: string;
  metric_type?: ProofMetricType;
  proof_type?: ProofType | null;
  proof_strength?: ProofStrength | null;
  time_window?: string;
  source_type?: ProofSourceType;
  source_ref?: string;
  claim_text_variants?: ProofClaimTextVariants;
  source_verification?: ProofSourceVerification;
  source_period_start?: string;
  source_period_end?: string;
  location_ids?: string[];
  anonymization?: ProofAnonymization;
  jurisdiction_tags?: string[];
  channels_allowed?: string[];
  value_areas?: string[];
  outreach_approved_by?: string | null;
  outreach_approved_at?: string | null;
  outreach_approval_basis_ref?: string | null;
  outreach_scope?: ProofOutreachScope | null;
  outreach_clearance_notes?: string | null;
  outreach_revoked_at?: string | null;
  named_use_approval_ref?: string | null;
  public_slot?: PublicProofSlot | null;
  public_payload?: StoredPublicProofPayload;
  public_approved_by?: string[];
  public_approved_at?: string | null;
  public_approval_basis_ref?: string | null;
  expires_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  content_queue_ids?: string[];
}

export interface ReviewDue {
  id: string;
  location_id: string;
  review_type: ReviewDueType;
  due_date: string;
  owner: string | null;
  status: ReviewDueStatus;
  meeting_date: string | null;
  agenda_text: string;
  talk_points: string;
  proof_metric_ids: string[];
  trigger_source: ReviewDueTriggerSource;
  trigger_key: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReviewDueInsert {
  location_id: string;
  review_type: ReviewDueType;
  due_date: string;
  owner?: string | null;
  status?: ReviewDueStatus;
  meeting_date?: string | null;
  agenda_text?: string;
  talk_points?: string;
  proof_metric_ids?: string[];
  trigger_source?: ReviewDueTriggerSource;
  trigger_key?: string | null;
}

export interface ReviewDueUpdate {
  review_type?: ReviewDueType;
  due_date?: string;
  owner?: string | null;
  status?: ReviewDueStatus;
  meeting_date?: string | null;
  agenda_text?: string;
  talk_points?: string;
  proof_metric_ids?: string[];
}

export interface ProofMetricListFilters {
  status?: ProofMetricStatus | ProofMetricStatus[];
  locationId?: string;
  proofMetricId?: string;
  sourceRef?: string;
  channel?: string;
  publicSlot?: PublicProofSlot;
}

export interface ReviewDueListFilters {
  status?: ReviewDueStatus | ReviewDueStatus[];
  reviewType?: ReviewDueType | ReviewDueType[];
  locationId?: string;
  owner?: string;
  dueBefore?: string;
  dueAfter?: string;
}

export const reviewDueTypeLabels: Record<ReviewDueType, string> = {
  improvement_campaign_readout: 'Improvement campaign readout',
  stabilization: 'Stabilization',
  qbr: 'Quarterly QBR',
  renewal_planning: 'Renewal planning',
  at_risk_review: 'At-risk review',
};

export const reviewDueStatusLabels: Record<ReviewDueStatus, string> = {
  open: 'Open',
  scheduled: 'Scheduled',
  completed: 'Completed',
};

export const reviewDueTriggerSourceLabels: Record<ReviewDueTriggerSource, string> = {
  manual: 'Manual',
  auto: 'Auto-synced',
};

export type AtRiskSignalType =
  | 'renewal_pending'
  | 'delayed'
  | 'urgent_launch'
  | 'contract_renewal_soon'
  | 'health_score_low'
  | 'pulse_delivery_gap'
  | 'commercial_gap'
  | 'overdue_review'
  | 'competitive_eval'
  | 'gates_stalled'
  | 'other';

export type AtRiskFlagSource = 'computed' | 'manual';

export type AtRiskFlagStatus = 'open' | 'monitoring' | 'resolved';

export interface LocationAtRiskFlag {
  id: string;
  location_id: string;
  signal_type: AtRiskSignalType;
  source: AtRiskFlagSource;
  status: AtRiskFlagStatus;
  owner: string | null;
  next_action: string | null;
  notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface LocationAtRiskFlagInsert {
  location_id: string;
  signal_type: AtRiskSignalType;
  source: AtRiskFlagSource;
  status?: AtRiskFlagStatus;
  owner?: string | null;
  next_action?: string | null;
  notes?: string | null;
}

export interface LocationAtRiskFlagUpdate {
  status?: AtRiskFlagStatus;
  owner?: string | null;
  next_action?: string | null;
  notes?: string | null;
  resolved_at?: string | null;
}

export interface AtRiskFlagListFilters {
  status?: AtRiskFlagStatus | AtRiskFlagStatus[];
  signalType?: AtRiskSignalType | AtRiskSignalType[];
  source?: AtRiskFlagSource | AtRiskFlagSource[];
  locationId?: string;
  owner?: string;
}

export const COMPUTED_AT_RISK_SIGNAL_TYPES: readonly AtRiskSignalType[] = [
  'renewal_pending',
  'delayed',
  'urgent_launch',
  'contract_renewal_soon',
  'health_score_low',
  'pulse_delivery_gap',
  'commercial_gap',
  'overdue_review',
  'gates_stalled',
] as const;

export const MANUAL_AT_RISK_SIGNAL_TYPES: readonly AtRiskSignalType[] = [
  'competitive_eval',
  'gates_stalled',
  'other',
] as const;

export const atRiskSignalTypeLabels: Record<AtRiskSignalType, string> = {
  renewal_pending: 'Renewal pending',
  delayed: 'Delayed',
  urgent_launch: 'Launch within 7 days',
  contract_renewal_soon: 'Contract renewal ≤60d',
  health_score_low: 'Health score at-risk',
  pulse_delivery_gap: 'Pulse delivery gap',
  commercial_gap: 'Commercial data gap',
  overdue_review: 'Overdue review due',
  competitive_eval: 'Competitive evaluation',
  gates_stalled: 'Readiness gates stalled',
  other: 'Other',
};

export const atRiskFlagSourceLabels: Record<AtRiskFlagSource, string> = {
  computed: 'Auto (signals)',
  manual: 'Manual',
};

export const atRiskFlagStatusLabels: Record<AtRiskFlagStatus, string> = {
  open: 'Open',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

export type ContentQueueType =
  | 'product_launch'
  | 'incremental_feature'
  | 'data_highlight'
  | 'deployment_snapshot'
  | 'partnership_milestone'
  | 'ops_maturity'
  | 'event_trade_show'
  | 'competitive_category'
  | 'competitive_named'
  | 'other';

export type ContentQueueChannel = 'linkedin' | 'instagram';

export type ContentQueueStatus = 'draft' | 'approved' | 'scheduled' | 'published' | 'killed';

export type ContentQueueTriggerSource = 'manual' | 'proof_library' | 'release_note' | 'deployment';

export interface ContentQueueItem {
  id: string;
  title: string;
  content_type: ContentQueueType;
  channel: ContentQueueChannel;
  status: ContentQueueStatus;
  draft_text: string;
  approver: string | null;
  scheduled_for: string | null;
  published_url: string | null;
  killed_reason: string | null;
  proof_metric_ids: string[];
  location_id: string | null;
  jurisdiction_tag: string;
  trigger_source: ContentQueueTriggerSource;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentQueueInsert {
  title: string;
  content_type: ContentQueueType;
  channel: ContentQueueChannel;
  status?: ContentQueueStatus;
  draft_text?: string;
  approver?: string | null;
  scheduled_for?: string | null;
  published_url?: string | null;
  killed_reason?: string | null;
  proof_metric_ids?: string[];
  location_id?: string | null;
  jurisdiction_tag?: string;
  trigger_source?: ContentQueueTriggerSource;
  notes?: string | null;
}

export interface ContentQueueUpdate {
  title?: string;
  content_type?: ContentQueueType;
  channel?: ContentQueueChannel;
  status?: ContentQueueStatus;
  draft_text?: string;
  approver?: string | null;
  scheduled_for?: string | null;
  published_url?: string | null;
  killed_reason?: string | null;
  proof_metric_ids?: string[];
  location_id?: string | null;
  jurisdiction_tag?: string;
  trigger_source?: ContentQueueTriggerSource;
  notes?: string | null;
}

export interface ContentQueueListFilters {
  status?: ContentQueueStatus | ContentQueueStatus[];
  contentType?: ContentQueueType | ContentQueueType[];
  channel?: ContentQueueChannel | ContentQueueChannel[];
  locationId?: string;
}

export const contentQueueTypeLabels: Record<ContentQueueType, string> = {
  product_launch: 'Product / platform launch',
  incremental_feature: 'Incremental feature',
  data_highlight: 'Data highlight',
  deployment_snapshot: 'Deployment snapshot',
  partnership_milestone: 'Partnership milestone',
  ops_maturity: 'Ops / process maturity',
  event_trade_show: 'Event / trade show',
  competitive_category: 'Competitive (category POV)',
  competitive_named: 'Competitive (named vendor)',
  other: 'Other',
};

export const contentQueueChannelLabels: Record<ContentQueueChannel, string> = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
};

export const contentQueueStatusLabels: Record<ContentQueueStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
  killed: 'Killed',
};

export const contentQueueTriggerSourceLabels: Record<ContentQueueTriggerSource, string> = {
  manual: 'Manual',
  proof_library: 'Proof library',
  release_note: 'Release note',
  deployment: 'Deployment',
};

export const proofMetricStatusLabels: Record<ProofMetricStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Ready',
  ready: 'Ready',
  expired: 'Expired',
  revoked: 'Revoked',
  archived: 'Archived',
};

export const proofTypeLabels: Record<ProofType, string> = {
  performance_metric: 'Performance metric',
  benchmark: 'Benchmark',
  case_study: 'Case study',
  survey: 'Survey',
  customer_review: 'Customer review',
  qualitative_claim: 'Qualitative claim',
};

export const proofStrengthLabels: Record<ProofStrength, string> = {
  hard_metric: 'Hard metric',
  case_study: 'Case study',
  survey: 'Survey',
  social_proof: 'Social proof',
  qualitative: 'Qualitative',
};

export type PlacementAuditStatus =
  | 'requested'
  | 'scheduled'
  | 'in_field'
  | 'report_draft'
  | 'complete'
  | 'cancelled';

export type PlacementAuditTriggerReason =
  | 'low_engagement'
  | 'sold_sku'
  | 'kiosk_signal'
  | 'cs_recommendation'
  | 'other';

export interface PlacementAudit {
  id: string;
  location_id: string;
  status: PlacementAuditStatus;
  trigger_reason: PlacementAuditTriggerReason;
  owner: string | null;
  engagement_context: string;
  findings_notes: string;
  report_url: string | null;
  scheduled_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlacementAuditInsert {
  location_id: string;
  status?: PlacementAuditStatus;
  trigger_reason?: PlacementAuditTriggerReason;
  owner?: string | null;
  engagement_context?: string;
  findings_notes?: string;
  report_url?: string | null;
  scheduled_date?: string | null;
  completed_at?: string | null;
}

export interface PlacementAuditUpdate {
  status?: PlacementAuditStatus;
  trigger_reason?: PlacementAuditTriggerReason;
  owner?: string | null;
  engagement_context?: string;
  findings_notes?: string;
  report_url?: string | null;
  scheduled_date?: string | null;
  completed_at?: string | null;
}

export const PLACEMENT_AUDIT_OPEN_STATUSES: readonly PlacementAuditStatus[] = [
  'requested',
  'scheduled',
  'in_field',
  'report_draft',
];

export const placementAuditStatusLabels: Record<PlacementAuditStatus, string> = {
  requested: 'Requested',
  scheduled: 'Scheduled',
  in_field: 'In field',
  report_draft: 'Report draft',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

export const placementAuditTriggerLabels: Record<PlacementAuditTriggerReason, string> = {
  low_engagement: 'Low engagement',
  sold_sku: 'Sold SKU (AUDIT-PLACE)',
  kiosk_signal: 'Kiosk signal',
  cs_recommendation: 'CS recommendation',
  other: 'Other',
};
